import {
  ActionIcon,
  Paper,
  Select,
  Stack,
  Text,
  Title,
  type ComboboxData
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useParams } from "react-router";
import { useOperators } from "../../../../hooks/useOperators";
import { OperatorFeatures } from "../../../../urls";
import type { Meter, Site } from "../../../../utils/backend-types";
import { DataImportRequestType } from "../../../../utils/enums";
import { splitNameExtension } from "../../../../utils/files/splitNameExtension";
import {
  METER_OPTIONAL_TEXT,
  getMeterDisplayName,
  getMeterGroupName
} from "../../../../utils/meter";
import { getTablerIconByFileExtension } from "../../../BuildingBlocks/Icon/utils/getTablerIconByFileExtension";
import { dataTypeChoices, type EnergyDataFile } from "../EnergyDataUpload";
import "./FileList.scss";

const MANY_METERS_VALUE = "many";

interface FileListProps {
  energyDataFiles: Array<EnergyDataFile>;
  meters: Array<Meter>;
  sites: Array<Site>;
  onChangeFileMeter: (
    index: number,
    meterId: number | null,
    manySelected: boolean
  ) => void;
  onChangeFileDataType: (
    index: number,
    typeOfData: DataImportRequestType
  ) => void;
  onRemoveFile: (index: number) => void;
}

function FileList({
  energyDataFiles,
  meters,
  sites,
  onChangeFileMeter,
  onChangeFileDataType,
  onRemoveFile
}: FileListProps) {
  return (
    <div className="FileList">
      {energyDataFiles.length > 0 && (
        <Title order={6}>Ausgewählte Dateien</Title>
      )}
      <ul>
        {energyDataFiles.map((file, index) => (
          <EnergyDataFileRow
            initialDataType={file.typeOfData}
            key={file.id}
            meters={meters}
            name={file.name}
            sites={sites}
            onChangeDataType={(typeOfData) =>
              onChangeFileDataType(index, typeOfData)
            }
            onChangeMeter={(meterId, manySelected) =>
              onChangeFileMeter(index, meterId, manySelected)
            }
            onRemoveFile={() => onRemoveFile(index)}
          />
        ))}
      </ul>
    </div>
  );
}

interface EnergyDataFileRowProps {
  initialDataType: DataImportRequestType | null;
  name: string;
  meters: Array<Meter>;
  sites: Array<Site>;
  onChangeMeter: (meterId: number | null, manySelected: boolean) => void;
  onChangeDataType: (typeOfData: DataImportRequestType) => void;
  onRemoveFile: () => void;
}

function EnergyDataFileRow({
  initialDataType,
  name,
  meters,
  sites,
  onChangeMeter,
  onChangeDataType,
  onRemoveFile
}: EnergyDataFileRowProps) {
  const { projectId } = useParams();

  const { data: revenueMonitoringOperators } = useOperators({
    activeFeatures: [OperatorFeatures.RevenueMonitoring],
    project_id: projectId
  });

  const isRevenueMonitoringActive =
    revenueMonitoringOperators && revenueMonitoringOperators.length > 0;

  const [isMeterDropdownHidden, setIsMeterDropdownHidden] = useState(
    initialDataType !== DataImportRequestType.METERING
  );
  const [meterDropDownKey, setMeterDropDownKey] = useState<string>();

  const meterGroups = Array.from(
    new Set(meters.map((meter) => getMeterGroupName(meter.site, sites)))
  );
  const meterOptions: ComboboxData = [
    {
      group: "",
      items: [{ label: METER_OPTIONAL_TEXT, value: MANY_METERS_VALUE }]
    },
    ...meterGroups.map((group) => ({
      group: group,
      items: meters
        .filter((meter) => getMeterGroupName(meter.site, sites) === group)
        .map((meter) => ({
          label: getMeterDisplayName(meter),
          value: meter.id.toString()
        }))
    }))
  ];

  const [, fileExtension] = splitNameExtension(name);
  const TablerIcon = getTablerIconByFileExtension(fileExtension);

  function handleDataTypeDropdownChange(value: string | null) {
    if (
      !value ||
      !Object.values(DataImportRequestType).includes(
        value as DataImportRequestType
      )
    ) {
      return;
    }
    setIsMeterDropdownHidden(value !== DataImportRequestType.METERING);
    onChangeDataType(value as DataImportRequestType);
    setMeterDropDownKey(value);
  }

  function handleMeterDropdownChange(value: string | null): void {
    if (value === "" || value === null) {
      onChangeMeter(null, false);
    } else if (value === MANY_METERS_VALUE) {
      onChangeMeter(null, true);
    } else {
      onChangeMeter(parseInt(value, 10), false);
    }
  }

  return (
    <li className="file">
      <Paper p="xs">
        <div className="file-body">
          <TablerIcon color="green" />
          <Text className="name" ml="xs">
            {name}
          </Text>
          <Stack gap="xs" miw={300}>
            {isRevenueMonitoringActive && (
              <Select
                data={dataTypeChoices}
                defaultValue={initialDataType ?? undefined}
                id="data_type"
                name="data_type"
                required
                onChange={handleDataTypeDropdownChange}
              />
            )}
            <Select
              clearable
              data={meterOptions}
              defaultValue={MANY_METERS_VALUE}
              disabled={isMeterDropdownHidden}
              id="meter"
              key={meterDropDownKey}
              name="meter"
              placeholder={METER_OPTIONAL_TEXT}
              required
              onChange={handleMeterDropdownChange}
            />
          </Stack>
          <ActionIcon
            bd="none"
            ml="sm"
            size="xs"
            variant="default"
            onClick={onRemoveFile}
          >
            <IconX />
          </ActionIcon>
        </div>
      </Paper>
    </li>
  );
}

export { FileList, FileListProps };
